import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { Button, Icon, MenuSection } from '@folio/stripes/components';
import { useShowCallout } from '@folio/stripes-acq-components';

import { useStripes } from '@folio/stripes/core';
import { useCirculationLogExport } from './useCirculationLogExport';
import { useExcelExport } from '../../hooks/useExcelExport';
import ColumnSelectionModal from './ColumnSelectionModal';

const CirculationLogListActions = ({
  logEventsCount,
  onToggle,
  logEvents,
  servicePoints,
  columnMapping,
  visibleColumns,
  resultsFormatter,
  intl,
  loadAllLogEvents,
}) => {
  const { formatMessage } = useIntl();
  const showCallout = useShowCallout();
  const stripes = useStripes();
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  const { isLoading, requestExport } = useCirculationLogExport({
    onSuccess: () => {
      showCallout({
        message: formatMessage({
          id: 'ui-circulation-log.logEvents.actions.export.success',
        }),
      });
    },
    onError: () => {
      return showCallout({
        message: formatMessage({
          id: 'ui-circulation-log.logEvents.actions.export.error',
        }),
        type: 'error',
      });
    },
  });

  const { exportData, isExporting } = useExcelExport();

  const handleExcelExport = async (
    selectedColumns = visibleColumns,
    customReportName = 'circulation-log',
  ) => {
    // Show loading message for large datasets
    if (logEventsCount > logEvents.length) {
      showCallout({
        message: formatMessage(
          {
            id: 'ui-circulation-log.logEvents.actions.export.excel.loading',
          },
          {
            total: logEventsCount,
          },
        ),
        type: 'info',
      });
    }

    // Get all log events for export using batch processing
    let allLogEvents = logEvents;

    if (logEventsCount > logEvents.length && loadAllLogEvents) {
      try {
        allLogEvents = await loadAllLogEvents();

        // Show success message after loading all data
        showCallout({
          message: formatMessage(
            {
              id: 'ui-circulation-log.logEvents.actions.export.excel.dataLoaded',
            },
            {
              total: allLogEvents.length,
            },
          ),
          type: 'success',
        });
      } catch (error) {
        showCallout({
          message: formatMessage({
            id: 'ui-circulation-log.logEvents.actions.export.excel.error',
          }),
          type: 'error',
        });

        return;
      }
    }

    const servicePointsMap = servicePoints.reduce((acc, sp) => {
      acc[sp.id] = sp.name;

      return acc;
    }, {});

    // Convert columnMapping to simple strings
    const simpleColumnMapping = {
      userBarcode: formatMessage({ id: 'ui-circulation-log.logEvent.user' }),
      itemBarcode: formatMessage({ id: 'ui-circulation-log.logEvent.item' }),
      object: formatMessage({ id: 'ui-circulation-log.logEvent.object' }),
      action: formatMessage({ id: 'ui-circulation-log.logEvent.action' }),
      date: formatMessage({ id: 'ui-circulation-log.logEvent.date' }),
      servicePoint: formatMessage({
        id: 'ui-circulation-log.logEvent.servicePoint',
      }),
      source: formatMessage({ id: 'ui-circulation-log.logEvent.source' }),
      description: formatMessage({
        id: 'ui-circulation-log.logEvent.description',
      }),
      actions: formatMessage({ id: 'ui-circulation-log.logEvent.actions' }),
    };

    const translations = {
      platformTitle: formatMessage({
        id: 'ui-circulation-log.excel.platformTitle',
      }),
      systemTitle: formatMessage({
        id: 'ui-circulation-log.excel.systemTitle',
      }),
      moduleTitle: formatMessage({
        id: 'ui-circulation-log.excel.moduleTitle',
      }),
      generatedDate: formatMessage({
        id: 'ui-circulation-log.excel.generatedDate',
      }),
      generatedTime: formatMessage({
        id: 'ui-circulation-log.excel.generatedTime',
      }),
      totalRecords: formatMessage({
        id: 'ui-circulation-log.excel.totalRecords',
      }),
      reportType: formatMessage({ id: 'ui-circulation-log.excel.reportType' }),
      reportTypeValue: formatMessage({
        id: 'ui-circulation-log.excel.reportTypeValue',
      }),
      systemVersion: formatMessage({
        id: 'ui-circulation-log.excel.systemVersion',
      }),
      generatedBy: formatMessage({
        id: 'ui-circulation-log.excel.generatedBy',
      }),
      generatedByValue: formatMessage({
        id: 'ui-circulation-log.excel.generatedByValue',
      }),
      disclaimer: formatMessage({ id: 'ui-circulation-log.excel.disclaimer' }),
      copyright: formatMessage({ id: 'ui-circulation-log.excel.copyright' }),
    };

    // Apply formatter to get display values like in the table
    const filteredData = allLogEvents.map((event) => {
      const filteredEvent = {};

      selectedColumns.forEach((col) => {
        if (col === 'actions') {
          // Skip actions column as it's UI-only
          return;
        }

        // Handle special columns with custom logic
        if (col === 'itemBarcode') {
          // Extract barcodes from items array
          if (event.items && Array.isArray(event.items)) {
            filteredEvent[col] = event.items
              .map((item) => item.itemBarcode || item)
              .join(', ');
          } else {
            filteredEvent[col] = '';
          }
        } else if (col === 'date') {
          // Format date as readable string
          if (event.date) {
            filteredEvent[col] = new Date(event.date).toLocaleString();
          } else {
            filteredEvent[col] = '';
          }
        } else if (resultsFormatter[col]) {
          const formattedValue = resultsFormatter[col](event);

          // Handle React components by extracting text content
          if (
            formattedValue &&
            typeof formattedValue === 'object' &&
            formattedValue.props
          ) {
            // For FormattedMessage components, get the translated text
            if (formattedValue.props.id) {
              filteredEvent[col] = intl.formatMessage({
                id: formattedValue.props.id,
              });
            } else if (formattedValue.props.children) {
              filteredEvent[col] = formattedValue.props.children;
            } else {
              filteredEvent[col] = String(formattedValue);
            }
          } else {
            filteredEvent[col] = formattedValue || '';
          }
        } else if (Object.prototype.hasOwnProperty.call(event, col)) {
          // Regular columns that exist in data
          filteredEvent[col] = event[col];
        } else {
          // Column not found in data
          filteredEvent[col] = '';
        }
      });

      return filteredEvent;
    });

    const result = await exportData(
      filteredData,
      customReportName,
      servicePointsMap,
      translations,
      selectedColumns,
      simpleColumnMapping,
    );

    if (result.success) {
      showCallout({
        message: formatMessage({
          id: 'ui-circulation-log.logEvents.actions.export.excel.success',
        }),
      });
    } else {
      showCallout({
        message: formatMessage({
          id: 'ui-circulation-log.logEvents.actions.export.excel.error',
        }),
        type: 'error',
      });
    }
  };

  const isExportDisabled =
    !logEventsCount ||
    isLoading ||
    isExporting ||
    !stripes.hasPerm('ui-circulation-log.log-event.all');

  return (
    <>
      <MenuSection id="circulation-log-list-actions">
        <Button
          data-testid="export-results-csv"
          buttonStyle="dropdownItem"
          aria-label={formatMessage({
            id: 'ui-circulation-log.logEvents.actions.export',
          })}
          onClick={() => {
            onToggle();
            requestExport();
          }}
          disabled={isExportDisabled}
        >
          <Icon size="small" icon="download">
            {formatMessage({
              id: 'ui-circulation-log.logEvents.actions.export',
            })}
          </Icon>
        </Button>
        <Button
          data-testid="export-results-excel"
          buttonStyle="dropdownItem"
          aria-label={formatMessage({
            id: 'ui-circulation-log.logEvents.actions.export.excel',
          })}
          onClick={() => {
            setIsColumnModalOpen(true);
          }}
          disabled={isExportDisabled}
        >
          <Icon size="small" icon="download">
            {formatMessage({
              id: 'ui-circulation-log.logEvents.actions.export.excel',
            })}
          </Icon>
        </Button>
      </MenuSection>

      <ColumnSelectionModal
        open={isColumnModalOpen}
        onClose={() => {
          setIsColumnModalOpen(false);
          onToggle();
        }}
        onConfirm={(selectedColumns, reportName) => {
          setIsColumnModalOpen(false);
          onToggle();
          handleExcelExport(selectedColumns, reportName);
        }}
        availableColumns={visibleColumns}
        columnMapping={{
          userBarcode: formatMessage({
            id: 'ui-circulation-log.logEvent.user',
          }),
          itemBarcode: formatMessage({
            id: 'ui-circulation-log.logEvent.item',
          }),
          object: formatMessage({ id: 'ui-circulation-log.logEvent.object' }),
          action: formatMessage({ id: 'ui-circulation-log.logEvent.action' }),
          date: formatMessage({ id: 'ui-circulation-log.logEvent.date' }),
          servicePoint: formatMessage({
            id: 'ui-circulation-log.logEvent.servicePoint',
          }),
          source: formatMessage({ id: 'ui-circulation-log.logEvent.source' }),
          description: formatMessage({
            id: 'ui-circulation-log.logEvent.description',
          }),
          actions: formatMessage({ id: 'ui-circulation-log.logEvent.actions' }),
        }}
      />
    </>
  );
};

CirculationLogListActions.propTypes = {
  logEventsCount: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  logEvents: PropTypes.arrayOf(PropTypes.object),
  servicePoints: PropTypes.arrayOf(PropTypes.object),
  columnMapping: PropTypes.object,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  resultsFormatter: PropTypes.object,
  intl: PropTypes.object,
  loadAllLogEvents: PropTypes.func,
};

CirculationLogListActions.defaultProps = {
  logEvents: [],
  servicePoints: [],
  columnMapping: {},
  visibleColumns: [],
  resultsFormatter: {},
  intl: {},
  loadAllLogEvents: null,
};

export default CirculationLogListActions;
